import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';

import { CloseIcon, ErrorIcon, InfoIcon, SuccessIcon, WarningIcon } from '../internal/icons';
import { IconButton } from './IconButton';
import styles from './InlineNotification.module.css';

export type NotificationKind = 'error' | 'warning' | 'success' | 'info';

const ICON: Record<NotificationKind, ReactNode> = {
  error: <ErrorIcon />,
  warning: <WarningIcon />,
  success: <SuccessIcon />,
  info: <InfoIcon />,
};

/** The visible word. Colour is never the only channel. */
const WORD: Record<NotificationKind, string> = {
  error: 'Error',
  warning: 'Warning',
  success: 'Success',
  info: 'Info',
};

export interface InlineNotificationProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title' | 'children'> {
  kind: NotificationKind;
  title: ReactNode;
  /** Body copy. Keep it to what the reader must do next. */
  children?: ReactNode;
  /** Action row rendered under the body — usually a ghost Button or a Link. */
  actions?: ReactNode;
  onClose?: () => void;
  closeLabel?: string;
  /** Sit flush inside a panel: no outer border radius, full-bleed. */
  flush?: boolean;
  /** Hide the leading kind word when the surrounding context already states it. */
  hideKindWord?: boolean;
}

/**
 * Errors get `role="alert"` (assertive) because they interrupt a task;
 * everything else is `role="status"` so it does not talk over the user.
 */
export const InlineNotification = forwardRef<HTMLDivElement, InlineNotificationProps>(
  function InlineNotification(
    {
      kind,
      title,
      children,
      actions,
      onClose,
      closeLabel = 'Dismiss notification',
      flush = false,
      hideKindWord = false,
      className,
      ...rest
    },
    ref,
  ) {
    return (
      <div
        ref={ref}
        role={kind === 'error' ? 'alert' : 'status'}
        data-kind={kind}
        className={clsx(styles.root, styles[kind], flush && styles.flush, className)}
        {...rest}
      >
        <span className={styles.icon} data-kind={kind}>
          {ICON[kind]}
        </span>
        <div className={styles.content}>
          <p className={styles.title}>
            {!hideKindWord && <span className={styles.word}>{WORD[kind]}</span>}
            {title}
          </p>
          {children !== undefined && <div className={styles.body}>{children}</div>}
          {actions !== undefined && <div className={styles.actions}>{actions}</div>}
        </div>
        {onClose !== undefined && (
          <IconButton
            label={closeLabel}
            icon={<CloseIcon />}
            size="sm"
            onClick={onClose}
            className={styles.close}
          />
        )}
      </div>
    );
  },
);
